import { useState } from 'react'
import { Factory } from '@phosphor-icons/react'
import { Badge, Button, DataTable, EmptyState, type Column } from '@/ui'
import { istDateKey } from '@/lib/ist'
import { errorMessage } from '../api/core'
import { useMachines, useSteps } from '../api/processing'
import {
  fetchAllProcessRuns,
  RUN_STATUSES,
  useProcessRun,
  useProcessRuns,
  type MachineIdentifiedBy,
  type ProcessRunFilters,
  type ProcessRunRow,
} from '../api/processRuns'
import { DetailDrawer } from '../components/DetailDrawer'
import { DateFilter, FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage, QueryState } from '../components/Layout'
import { Timeline } from '../components/Timeline'
import { Code, Facts, Kg, RefLink, Section, Severity, Status, Stream, UserName, When } from '../components/bits'
import { humanize } from '../labels'
import { downloadText, toCsv } from '../lib/csv'
import { useUrlState } from '../lib/useUrlState'
import { IDENTIFIED_LABEL, identifiedLabel, PROCESS_RUNS_CSV_HEADER, processRunsCsvRows } from '../model/processRuns'

const LIMIT = 25

function Identified({ by }: { by: MachineIdentifiedBy | null }) {
  if (!by) return <span className="text-muted">—</span>
  return <Badge tone={by === 'QR_SCAN' ? 'ok' : 'warn'}>{identifiedLabel(by)}</Badge>
}

/**
 * Processing runs — the machine processing log (pilot): every run, on which machine, which step,
 * who ran and cleaned it, kilos in and out by stream, lots in and batches out, and whether the
 * machine's label was scanned at open. Filter by machine (Equipment → Runs), step, status, dates.
 */
export function ProcessRunsPage() {
  const [f, setF, { reset, active }] = useUrlState({
    equipmentId: '',
    stepCode: '',
    status: '',
    identifiedBy: '',
    from: '',
    to: '',
    page: 1,
    open: '',
  })
  const machines = useMachines()
  const steps = useSteps()
  const filters: ProcessRunFilters = {
    equipmentId: f.equipmentId || undefined,
    stepCode: f.stepCode || undefined,
    status: f.status || undefined,
    identifiedBy: (f.identifiedBy || undefined) as MachineIdentifiedBy | undefined,
    from: f.from || undefined,
    to: f.to || undefined,
  }
  const q = useProcessRuns({ ...filters, page: f.page, limit: LIMIT })
  const [exporting, setExporting] = useState(false)
  const machine = (machines.data ?? []).find((m) => m.id === f.equipmentId)
  const stepLabel = (code: string) => (steps.data ?? []).find((s) => s.code === code)?.label ?? humanize(code)

  const exportCsv = async () => {
    setExporting(true)
    try {
      const rows = await fetchAllProcessRuns(filters)
      downloadText(
        `process-runs-${machine?.code ?? 'all'}-${istDateKey()}.csv`,
        toCsv(PROCESS_RUNS_CSV_HEADER, processRunsCsvRows(rows)),
      )
    } finally {
      setExporting(false)
    }
  }

  const columns: Column<ProcessRunRow>[] = [
    {
      id: 'run',
      header: 'Run',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <Code>{r.code}</Code>
          <span className="text-[12px] text-muted">
            <When at={r.openedAt} />
          </span>
        </span>
      ),
    },
    {
      id: 'machine',
      header: 'Machine',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.equipmentName ?? '—'}</span>
          <span className="font-mono text-[12px] text-muted">{r.equipmentCode}</span>
        </span>
      ),
    },
    { id: 'step', header: 'Step', cell: (r) => stepLabel(r.stepCode), hideBelow: 'md' },
    { id: 'id', header: 'Machine check', cell: (r) => <Identified by={r.identifiedBy} />, hideBelow: 'md' },
    {
      id: 'operator',
      header: 'Operator',
      hideBelow: 'lg',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.operator?.name ?? '—'}</span>
          {r.cleanoutBy && <span className="text-[12px] text-muted">Cleanout: {r.cleanoutBy.name}</span>}
        </span>
      ),
    },
    {
      id: 'lots',
      header: 'Lots in',
      hideBelow: 'lg',
      cell: (r) =>
        r.lotsIn.length ? (
          <span className="flex flex-col leading-tight">
            {r.lotsIn.map((l) => (
              <span key={l.unitId}>
                <RefLink to={`/c/lots/${l.unitId}`}>{l.code}</RefLink>
                {l.farmerCode && <span className="text-[12px] text-muted"> · #{l.farmerCode}</span>}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    { id: 'in', header: 'In', align: 'right', cell: (r) => <Kg grams={r.inputG} /> },
    { id: 'out', header: 'Out', align: 'right', cell: (r) => <Kg grams={r.outputG} />, hideBelow: 'sm' },
    {
      id: 'residual',
      header: 'Residual',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (r) => (r.residualPct === null ? '—' : `${r.residualPct}%`),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1">
          <Status status={r.status} />
          {r.openExceptions > 0 && <Badge tone="danger">{r.openExceptions} open</Badge>}
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Processing"
      title={machine ? `Runs on ${machine.name} · ${machine.code}` : 'Processing runs'}
      description="Every run on every machine: who ran it, the lots that went in, the batches that came out, and whether the machine's label was scanned before processing."
      actions={
        <Button size="sm" variant="secondary" onClick={() => void exportCsv()} loading={exporting} disabled={!q.data?.total}>
          Export CSV
        </Button>
      }
    >
      <DataTable<ProcessRunRow>
        caption="Processing runs"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        onRowClick={(r) => setF({ open: r.id, page: f.page })}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Machine"
              value={f.equipmentId}
              onChange={(equipmentId) => setF({ equipmentId })}
              options={(machines.data ?? []).map((m) => ({ value: m.id, label: `${m.name} · ${m.code}` }))}
              className="min-w-[13rem]"
            />
            <SelectFilter
              label="Step"
              value={f.stepCode}
              onChange={(stepCode) => setF({ stepCode })}
              options={(steps.data ?? []).map((s) => ({ value: s.code, label: s.label }))}
            />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={RUN_STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
            />
            <SelectFilter
              label="Machine check"
              value={f.identifiedBy}
              onChange={(identifiedBy) => setF({ identifiedBy })}
              options={(Object.keys(IDENTIFIED_LABEL) as MachineIdentifiedBy[]).map((k) => ({
                value: k,
                label: IDENTIFIED_LABEL[k],
              }))}
            />
            <DateFilter label="From" value={f.from} onChange={(from) => setF({ from })} />
            <DateFilter label="To" value={f.to} onChange={(to) => setF({ to })} />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Factory />}
            title="No runs match"
            description="A run shows here once a machine operator's phone has synced its open run."
          />
        }
      />
      <RunDrawer id={f.open || null} onClose={() => setF({ open: '', page: f.page })} stepLabel={stepLabel} />
    </ConsolePage>
  )
}

const EVENT_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  RUN_OPENED: 'info',
  RUN_CLOSED: 'ok',
  RUN_CANCELLED: 'danger',
}

function RunDrawer({ id, onClose, stepLabel }: { id: string | null; onClose: () => void; stepLabel: (c: string) => string }) {
  const q = useProcessRun(id)
  const r = q.data
  return (
    <DetailDrawer
      open={Boolean(id)}
      onClose={onClose}
      width="lg"
      eyebrow="Processing run"
      title={r ? <Code>{r.code}</Code> : 'Run'}
      meta={
        r && (
          <>
            <Status status={r.status} />
            <Stream stream={r.stream} />
          </>
        )
      }
    >
      <QueryState query={q} label="this run">
        {r && (
          <div className="flex flex-col gap-4">
            <Facts
              items={[
                { label: 'Machine', value: `${r.equipmentName ?? '—'} · ${r.equipmentCode ?? ''}` },
                { label: 'Step', value: stepLabel(r.stepCode) },
                { label: 'Crop', value: r.cropName },
                {
                  label: 'Machine check',
                  value: (
                    <span className="flex flex-col gap-0.5">
                      <Identified by={r.identifiedBy} />
                      {r.machineLabel && <Code className="text-[12px]">{r.machineLabel}</Code>}
                    </span>
                  ),
                },
                { label: 'Operator', value: r.operator?.name },
                {
                  label: 'Cleanout',
                  value: r.cleanoutBy ? (
                    <span>
                      {r.cleanoutBy.name} · <When at={r.cleanoutAt} />
                    </span>
                  ) : (
                    '—'
                  ),
                },
                { label: 'Opened', value: <When at={r.openedAt} /> },
                { label: 'Closed', value: r.closedAt ? <When at={r.closedAt} /> : '—' },
                {
                  label: 'First lot',
                  value: r.originUnitId ? (
                    <RefLink to={`/c/lots/${r.originUnitId}`}>
                      {r.originUnitCode}
                      {r.originFarmerCode ? ` · #${r.originFarmerCode}` : ''}
                    </RefLink>
                  ) : (
                    '—'
                  ),
                },
              ]}
            />

            <Section title="Balance">
              <Facts
                columns={4}
                items={[
                  { label: 'In', value: <Kg grams={r.balance.inputG} /> },
                  { label: 'Product', value: <Kg grams={r.balance.productG} /> },
                  { label: 'By-product', value: <Kg grams={r.balance.byproductG} /> },
                  { label: 'Reject', value: <Kg grams={r.balance.rejectG} /> },
                  { label: 'Flush', value: <Kg grams={r.balance.flushG} /> },
                  { label: 'Out', value: <Kg grams={r.balance.outputG} /> },
                  {
                    label: 'Residual',
                    value: (
                      <span>
                        <Kg grams={r.balance.residualG} />
                        {r.balance.residualPct !== null && ` (${r.balance.residualPct}%)`}
                      </span>
                    ),
                  },
                  { label: 'Result', value: r.balance.result ? humanize(r.balance.result) : '—' },
                ]}
              />
            </Section>

            <Section title={`Bags in (${r.inputs.length})`}>
              {r.inputs.length === 0 ? (
                <p className="text-[14px] text-muted">No bag scanned in yet.</p>
              ) : (
                <ul className="divide-y divide-rule">
                  {r.inputs.map((i) => (
                    <li key={i.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-[14px]">
                      <span>
                        <Code>{i.tag ?? '—'}</Code> · <RefLink to={`/c/lots/${i.unitId}`}>{i.unitCode}</RefLink>
                        {i.farmerCode && <span className="text-muted"> · #{i.farmerCode}</span>}
                      </span>
                      <span className="flex items-baseline gap-3 text-[13px] text-muted">
                        <Kg grams={i.weightG} className="text-ink" />
                        <UserName id={i.scannedById} name={i.scannedBy?.name} />
                        <When at={i.scannedAt} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={`Outputs (${r.outputs.length})`}>
              {r.outputs.length === 0 ? (
                <p className="text-[14px] text-muted">Nothing tagged out yet.</p>
              ) : (
                <ul className="divide-y divide-rule">
                  {r.outputs.map((o) => (
                    <li key={o.id} className="py-1.5 text-[14px]">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span>
                          <RefLink to={`/c/lots/${o.id}`}>{o.code}</RefLink>
                          <span className="text-muted"> · {humanize(o.streamKey ?? o.unitType)}</span>
                        </span>
                        <span className="flex items-center gap-2">
                          <Kg grams={o.netG} />
                          <Status status={o.status} />
                          {o.hold && o.hold !== 'NONE' && <Badge tone="danger">{humanize(o.hold)}</Badge>}
                        </span>
                      </div>
                      {o.sacks.length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-x-3 font-mono text-[12px] text-muted">
                          {o.sacks.map((s, n) => (
                            <span key={`${s.tag}-${n}`}>
                              {s.tag ?? '—'} <Kg grams={s.netG} />
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section
              title={`Exceptions (${r.exceptions.length})`}
              actions={
                r.exceptions.length > 0 && (
                  <RefLink to={`/c/exceptions?runId=${r.id}&status=ALL`}>Open in Exceptions</RefLink>
                )
              }
            >
              {r.exceptions.length === 0 ? (
                <p className="text-[14px] text-muted">None.</p>
              ) : (
                <ul className="divide-y divide-rule">
                  {r.exceptions.map((x) => (
                    <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-[14px]">
                      <RefLink to={`/c/exceptions?runId=${r.id}&status=ALL&open=${x.id}`}>
                        {humanize(x.type)}
                        {x.kinds.length > 0 && <span className="text-muted"> · {x.kinds.map(humanize).join(', ')}</span>}
                      </RefLink>
                      <span className="flex items-center gap-2">
                        <Severity severity={x.severity} />
                        <Status status={x.status} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Trace">
              <Timeline
                items={r.events.map((e) => ({
                  id: e.id,
                  at: e.occurredAt,
                  tone: EVENT_TONE[e.bizStep] ?? 'neutral',
                  title: humanize(e.bizStep),
                  detail: (
                    <span className="flex flex-wrap gap-x-2">
                      <UserName id={e.actorId} />
                      {e.bizStep === 'RUN_OPENED' && typeof e.payload.machine_identified_by === 'string' && (
                        <span>· {identifiedLabel(e.payload.machine_identified_by as MachineIdentifiedBy)}</span>
                      )}
                    </span>
                  ),
                }))}
              />
            </Section>
          </div>
        )}
      </QueryState>
    </DetailDrawer>
  )
}
